import React, { useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Avatar,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  IconButton
} from "@material-ui/core";
import { Visibility, VisibilityOff, PhotoCamera } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import useWhatsApps from "../../hooks/useWhatsApps";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  btnWrapper: {
    position: "relative"
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  },
  formControl: {
    width: "100%"
  },
  avatarSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  avatar: {
    width: theme.spacing(10),
    height: theme.spacing(10)
  },
  metricCard: {
    padding: theme.spacing(2),
    height: "100%"
  },
  metricLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    textTransform: "uppercase"
  },
  metricValue: {
    fontSize: 24,
    fontWeight: 700
  },
  sectionTitle: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  hiddenInput: {
    display: "none"
  }
}));

const initialState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  schedule: "",
  greetingMessage: "",
  farewellMessage: "",
  signature: "",
  password: "",
  profile: "atendimento"
};

const UserModal = ({ open, onClose, userId }) => {
  const classes = useStyles();
  const { user: loggedInUser, updateCurrentUser } = useContext(AuthContext);
  const { loading, whatsApps } = useWhatsApps();
  const [formData, setFormData] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [whatsappId, setWhatsappId] = useState("");
  const [metrics, setMetrics] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingUser, setIsFetchingUser] = useState(false);
  const [loadedProfile, setLoadedProfile] = useState("");
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const fileInputRef = useRef(null);
  const isAdmin = loggedInUser.profile === "admin";

  useEffect(() => {
    if (!open) {
      return;
    }

    const fetchUser = async () => {
      if (!userId) {
        const createFormState = {
          ...initialState,
          profile: isAdmin ? "atendimento" : loggedInUser.profile || "atendimento"
        };

        setIsFetchingUser(false);
        setLoadedProfile(createFormState.profile);
        setFormData(createFormState);
        setSelectedQueueIds([]);
        setWhatsappId("");
        setMetrics(null);
        setSelectedPhoto(null);
        setPhotoPreview("");
        return;
      }

      setIsFetchingUser(true);
      try {
        const [{ data }, metricsResponse] = await Promise.all([
          api.get(`/users/${userId}`),
          api.get(`/users/${userId}/metrics`)
        ]);

        setFormData({
          firstName: data.firstName || "",
          lastName: data.lastName || "",
          email: data.email || "",
          phone: data.phone || "",
          schedule: data.schedule || "",
          greetingMessage: data.greetingMessage || "",
          farewellMessage: data.farewellMessage || "",
          signature: data.signature || "",
          password: "",
          profile: data.profile || "atendimento"
        });
        setLoadedProfile(data.profile || "atendimento");
        setSelectedQueueIds(data.queues?.map(queue => queue.id) || []);
        setWhatsappId(data.whatsappId || "");
        setPhotoPreview(data.profileImageUrl || "");
        setMetrics(metricsResponse.data);
      } catch (err) {
        toastError(err);
      } finally {
        setIsFetchingUser(false);
      }
    };

    fetchUser();
  }, [open, userId, isAdmin, loggedInUser.profile]);

  const handleClose = () => {
    setFormData(initialState);
    setSelectedQueueIds([]);
    setWhatsappId("");
    setMetrics(null);
    setSelectedPhoto(null);
    setPhotoPreview("");
    setLoadedProfile("");
    setIsFetchingUser(false);
    onClose();
  };

  const handleChange = event => {
    const { name, value } = event.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleChoosePhoto = event => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setSelectedPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const uploadPhotoIfNeeded = async savedUser => {
    if (!selectedPhoto) {
      return savedUser;
    }

    const formDataPhoto = new FormData();
    formDataPhoto.append("file", selectedPhoto);

    const { data } = await api.post(
      `/users/${savedUser.id}/photo`,
      formDataPhoto
    );

    return data;
  };

  const handleSaveUser = async () => {
    if (isFetchingUser) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        schedule: formData.schedule,
        greetingMessage: formData.greetingMessage,
        farewellMessage: formData.farewellMessage,
        signature: formData.signature,
        whatsappId: whatsappId || null,
        queueIds: selectedQueueIds
      };

      if (formData.password) {
        payload.password = formData.password;
      }

      if (isAdmin) {
        if (!userId) {
          payload.profile = formData.profile || "atendimento";
        } else if (formData.profile && formData.profile !== loadedProfile) {
          payload.profile = formData.profile;
        }
      }

      const response = userId
        ? await api.put(`/users/${userId}`, payload)
        : await api.post("/users", payload);

      const persistedUser = await uploadPhotoIfNeeded(response.data);

      if (
        persistedUser?.id &&
        loggedInUser?.id &&
        Number(persistedUser.id) === Number(loggedInUser.id)
      ) {
        updateCurrentUser(persistedUser);
      }

      toast.success(userId ? "Usuario atualizado." : "Usuario criado.");
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderMetric = (label, value) => (
    <Grid item xs={12} sm={4}>
      <Paper variant="outlined" className={classes.metricCard}>
        <div className={classes.metricLabel}>{label}</div>
        <div className={classes.metricValue}>{value ?? 0}</div>
      </Paper>
    </Grid>
  );

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          {userId ? "Editar usuario" : "Novo usuario"}
        </DialogTitle>
        <DialogContent dividers>
          <div className={classes.avatarSection}>
            <Avatar src={photoPreview} className={classes.avatar}>
              {(formData.firstName || formData.email || "U").charAt(0)}
            </Avatar>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleChoosePhoto}
                className={classes.hiddenInput}
              />
              <Button
                variant="outlined"
                startIcon={<PhotoCamera />}
                onClick={() => fileInputRef.current?.click()}
              >
                Escolher foto
              </Button>
              <Typography variant="body2" color="textSecondary">
                Foto de perfil do usuario.
              </Typography>
            </div>
          </div>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nome"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sobrenome"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Telefone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="password"
                variant="outlined"
                margin="dense"
                label="Senha"
                value={formData.password}
                onChange={handleChange}
                type={showPassword ? "text" : "password"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(prev => !prev)}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl
                variant="outlined"
                margin="dense"
                className={classes.formControl}
              >
                <InputLabel id="profile-selection-input-label">Cargo</InputLabel>
                <Select
                  labelId="profile-selection-input-label"
                  name="profile"
                  value={formData.profile}
                  onChange={handleChange}
                  label="Cargo"
                  disabled={!isAdmin}
                >
                  <MenuItem value="admin">Administrador</MenuItem>
                  <MenuItem value="financeiro">Financeiro</MenuItem>
                  <MenuItem value="atendimento">Atendimento</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Assinatura personalizada"
                name="signature"
                value={formData.signature}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
                fullWidth
                multiline
                rows={3}
                placeholder="Se vazio, o sistema usa Nome | Cargo automaticamente."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Agenda no perfil"
                name="schedule"
                value={formData.schedule}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
                fullWidth
                multiline
                rows={4}
                placeholder="Compromissos, rotina, observacoes e informacoes de agenda."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Mensagem de inicio"
                name="greetingMessage"
                value={formData.greetingMessage}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
                fullWidth
                multiline
                rows={4}
                placeholder="Mensagem padrao para abrir o atendimento."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Mensagem de despedida"
                name="farewellMessage"
                value={formData.farewellMessage}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
                fullWidth
                multiline
                rows={4}
                placeholder="Mensagem padrao para encerrar o atendimento."
              />
            </Grid>
            {isAdmin && (
              <Grid item xs={12}>
                <QueueSelect
                  selectedQueueIds={selectedQueueIds}
                  onChange={values => setSelectedQueueIds(values)}
                />
              </Grid>
            )}
            {isAdmin && !loading && (
              <Grid item xs={12}>
                <FormControl
                  variant="outlined"
                  margin="dense"
                  className={classes.formControl}
                >
                  <InputLabel>Agenda padrao do WhatsApp</InputLabel>
                  <Select
                    value={whatsappId}
                    onChange={event => setWhatsappId(event.target.value)}
                    label="Agenda padrao do WhatsApp"
                  >
                    <MenuItem value="">&nbsp;</MenuItem>
                    {whatsApps.map(whatsapp => (
                      <MenuItem key={whatsapp.id} value={whatsapp.id}>
                        {whatsapp.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>

          {metrics && (
            <>
              <Typography variant="h6" className={classes.sectionTitle}>
                Metricas de atendimento
              </Typography>
              <Grid container spacing={2}>
                {renderMetric("Total", metrics.totalTickets)}
                {renderMetric("Em aberto", metrics.openTickets)}
                {renderMetric("Pendentes", metrics.pendingTickets)}
                {renderMetric("Fechados", metrics.closedTickets)}
                {renderMetric("Resolvidos hoje", metrics.resolvedToday)}
                {renderMetric("Tarefas pendentes", metrics.pendingTasks)}
                {renderMetric("Tarefas atrasadas", metrics.overdueTasks)}
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            color="secondary"
            disabled={isSubmitting}
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveUser}
            color="primary"
            disabled={isSubmitting || isFetchingUser}
            variant="contained"
            className={classes.btnWrapper}
          >
            {userId ? "Salvar" : "Criar"}
            {isSubmitting && (
              <CircularProgress size={24} className={classes.buttonProgress} />
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UserModal;
